import { useEffect } from "react";
import { motion } from "framer-motion";
import { Zap, Shield, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageMeta from "@/components/PageMeta";
import { ContactPageJsonLd } from "@/components/JsonLd";
import { useGlowRef } from "@/hooks/use-glow-ref";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" as const },
  }),
};

const ContactPage = () => {
  const { t } = useTranslation();
  const embedGlow = useGlowRef();

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.data && e.data.type === "bookcall-resize") {
        const iframe = document.getElementById("bookcall-embed") as HTMLIFrameElement | null;
        if (iframe) iframe.style.height = e.data.height + "px";
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <PageMeta
        title="Contact — rawResponse {Labs}"
        description="Book a call with rawResponse {Labs}. We help agencies and enterprises monitor and optimize their brand visibility across ChatGPT, Perplexity, Claude and Gemini."
      />
      <ContactPageJsonLd />
      <Navbar />
      <main>


      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] radial-spot pointer-events-none opacity-60" />

        <div className="container max-w-6xl relative z-10">
          {/* Messaging header */}
          <motion.div initial="hidden" animate="visible" className="text-center mb-14 max-w-2xl mx-auto">
            <motion.p
              custom={0}
              variants={fadeUp}
              className="text-[11px] font-mono text-primary uppercase tracking-widest mb-4"
            >
              {t("contact.label")}
            </motion.p>
            <motion.h1
              custom={1}
              variants={fadeUp}
              className="text-[clamp(2rem,5vw,3.25rem)] font-semibold tracking-tight leading-[1.08] mb-6"
            >
              {t("contact.headline")}
              <br />
              <span className="text-muted-foreground">{t("contact.headlineMuted")}</span>
            </motion.h1>
            <motion.p
              custom={2}
              variants={fadeUp}
              className="text-[14px] font-mono text-muted-foreground leading-relaxed"
            >
              {t("contact.description")}
            </motion.p>

            {/* Trust signals */}
            <motion.div custom={3} variants={fadeUp} className="flex flex-wrap justify-center gap-6 mt-8">
              {[
                { icon: Zap, text: t("contact.trust1") },
                { icon: Shield, text: t("contact.trust2") },
                { icon: Sparkles, text: t("contact.trust3") },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <item.icon size={13} className="text-primary" />
                  </div>
                  <p className="text-[12px] font-mono text-foreground/80">{item.text}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* BookCall embed */}
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.6, ease: "easeOut" }}
            className="w-full"
          >
            <div ref={embedGlow} className="relative card-glow-strong mx-auto" style={{ maxWidth: 800 }}>
              <iframe
                id="bookcall-embed"
                src="https://bookcall.io/embed/rawresponselabs?layout=booking"
                width="800"
                height="800"
                scrolling="no"
                style={{ border: "none", maxWidth: "100%", display: "block" }}
                title="Book a call with rawResponse {Labs}"
              />
            </div>
          </motion.div>
        </div>
      </section>

      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
